import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useUrlStore } from "utils/useUrlStore";

// use FUTURE_MEDICAL as a single value that the backend will expand
const FUTURE_MEDICAL = ["FUTURE_MEDICAL"];
const LIEN_SERVICE_TYPES = ["LIEN"];
const DOCUMENT_MANAGEMENT_SERVICE_TYPES = ["DOCS"];

type WorkspaceType = "FM" | "LIEN" | "DOC_MGMT" | "OPERATIONS";

/**
 * hook to detect current workspace from URL and provide default service type filters
 * for auto-filtering documents based on workspace context.
 */
export const useWorkspaceAutoFilters = () => {
  const location = useLocation();
  const { getFilterValues } = useUrlStore();

  // detect workspace from URL path
  const workspaceType: WorkspaceType = useMemo(() => {
    const path = location.pathname;

    if (path.includes("/future-medical/")) return "FM";
    if (path.includes("/liens/")) return "LIEN";
    if (path.includes("/document-management/")) return "DOC_MGMT";
    if (path.includes("/operations/files/")) return "OPERATIONS";

    return "OPERATIONS"; // default fallback
  }, [location.pathname]);

  // get default service types based on workspace
  const defaultServiceTypes = useMemo(() => {
    switch (workspaceType) {
      case "FM":
        return FUTURE_MEDICAL; // Backend will expand this to all FM service types
      case "LIEN":
        return LIEN_SERVICE_TYPES;
      case "DOC_MGMT":
        return DOCUMENT_MANAGEMENT_SERVICE_TYPES;
      case "OPERATIONS":
      default:
        return []; // No default filter for Operations
    }
  }, [workspaceType]);

  // check if we should auto-apply filters
  // only apply if there are no existing service_type filters in the URL
  const shouldAutoApply = useMemo(() => {
    const existingServiceTypeFilters = getFilterValues("service_type");
    return existingServiceTypeFilters.length === 0;
  }, [getFilterValues]);

  return {
    workspaceType,
    defaultServiceTypes,
    shouldAutoApply,
  };
};
